import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";
import { getSettings } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1620275765334-4ed948bb4502?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxwYXBlciUyMG5vdGVib29rJTIwZGVza3xlbnwwfHx8fDE3ODYyODEyNTd8MA&ixlib=rb-4.1.0&q=85";
const ECO_IMG = "https://customer-assets.emergentagent.com/job_revamp-2024/artifacts/0izh8zwp_8908030416025_f.png";

const STATS = [
  { value: "10+", label: "Years of craft" },
  { value: "150+", label: "Schools supplied" },
  { value: "1,500+", label: "Retail outlets" },
  { value: "100%", label: "Made in India" },
];

const HEADLINE = [
  { text: "Write Green,", accent: false },
  { text: "Live Clean.", accent: true },
];

export const Hero = () => {
  const ref = useRef(null);
  const [heroIntro, setHeroIntro] = useState(
    "Premium notebooks, sketch pads and journals — crafted end-to-end under one roof on FSC-certified and recyclable kraft paper. From our desk to yours."
  );

  useEffect(() => {
    getSettings()
      .then((s) => {
        if (s.hero_intro) setHeroIntro(s.hero_intro);
      })
      .catch(() => {});
  }, []);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgRotate = useTransform(scrollYProgress, [0, 1], [0, 5]);
  const ghostY = useTransform(scrollYProgress, [0, 1], [0, -90]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden">
      <motion.span
        aria-hidden
        style={{ y: ghostY }}
        className="pointer-events-none select-none absolute -top-8 left-0 font-serif italic font-semibold text-[22vw] leading-none text-foreground/[0.04] whitespace-nowrap"
      >
        Classina
      </motion.span>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-20 md:pb-28 grid lg:grid-cols-12 gap-12 items-center relative">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-xs uppercase tracking-[0.25em] font-bold text-primary mb-6 flex items-center gap-2"
          >
            <Sparkles className="w-3.5 h-3.5" /> Eco-conscious stationery
          </motion.p>

          <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl font-semibold tracking-tighter leading-[1.02]">
            {HEADLINE.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.accent ? "italic text-primary" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.2 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-8 text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl"
          >
            {heroIntro}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#shop"
              data-testid="hero-shop-button"
              className="rounded-full bg-primary text-primary-foreground px-8 py-3.5 text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl inline-flex items-center gap-2"
            >
              Shop the collection <ArrowDown className="w-4 h-4" />
            </a>
            <a
              href="#trade"
              data-testid="hero-trade-button"
              className="rounded-full border border-foreground/20 px-8 py-3.5 text-sm font-semibold hover:bg-secondary transition-colors duration-300"
            >
              Bulk & school orders
            </a>
          </motion.div>

          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-8">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 + i * 0.1, duration: 0.6 }}
              >
                <p className="font-serif text-3xl md:text-4xl font-semibold text-foreground">{s.value}</p>
                <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
          className="lg:col-span-5 relative"
        >
          <motion.div style={{ y: imgY, rotate: imgRotate }} className="relative rounded-[2rem] overflow-hidden shadow-2xl max-h-[70vh]">
            <img src={HERO_IMG} alt="Notebook and pen on a desk" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-primary/10" />
          </motion.div>
          <motion.a
            href="#shop"
            data-testid="hero-eco-card"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="absolute -bottom-6 -left-4 sm:-left-10"
          >
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="bg-card rounded-2xl shadow-xl border border-border/60 p-4 flex items-center gap-4 hover:-translate-y-1 transition-transform duration-500"
            >
              <img src={ECO_IMG} alt="Classina A4 Eco Series" className="w-16 h-16 object-cover rounded-xl" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-accent">New launch</p>
                <p className="font-serif font-semibold text-sm mt-0.5">A4 Eco Series</p>
                <p className="text-xs text-muted-foreground">Recyclable kraft · ₹120</p>
              </div>
            </motion.div>
          </motion.a>
        </motion.div>
      </div>

      <div className="border-y border-border/60 bg-secondary/40 py-4 overflow-hidden">
        <div className="animate-marquee whitespace-nowrap flex gap-12 w-max">
          {[...Array(2)].flatMap((_, k) =>
            ["Write Green, Live Clean", "FSC-Certified Paper", "Recyclable Kraft", "Made in India", "SBK Manufacturing Co."].map((t, i) => (
              <span key={`${k}-${i}`} className="font-serif italic text-lg text-muted-foreground flex items-center gap-12">
                {t} <span className="text-accent not-italic">✦</span>
              </span>
            )),
          )}
        </div>
      </div>
    </section>
  );
};
